<template>
  <div class="inpaint-canvas-wrap">
    <!-- 上传区：未上传时显示 -->
    <div
      v-if="!imageLoaded"
      class="inpaint-upload"
      @click="$refs.fileInput.click()"
      @dragover.prevent
      @drop.prevent="onDrop"
    >
      <input type="file" ref="fileInput" accept="image/*" style="display:none" @change="handleFile">
      <div class="inpaint-upload-placeholder">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
        </svg>
        <p>{{ placeholderText }}</p>
        <span>JPG, PNG, WebP</span>
      </div>
    </div>

    <!-- 涂抹区：已上传后显示 -->
    <div v-else class="inpaint-editor">
      <!-- 画布容器：原图 + 涂抹层叠在一起 -->
      <div
        class="inpaint-stage"
        ref="stage"
        :style="{ width: displayWidth + 'px', height: displayHeight + 'px' }"
      >
        <img ref="baseImg" class="inpaint-base-img" :src="imageSrc" draggable="false">
        <!-- 涂抹层：用白色画，再用 CSS 混色显示成半透明红，导出时单独取透明度 -->
        <canvas
          ref="drawCanvas"
          class="inpaint-draw-canvas"
          :width="naturalWidth"
          :height="naturalHeight"
          :style="{ width: displayWidth + 'px', height: displayHeight + 'px' }"
          @mousedown="startDraw"
          @mousemove="onMove"
          @mouseup="endDraw"
          @mouseleave="endDraw"
          @touchstart.prevent="startDraw"
          @touchmove.prevent="onMove"
          @touchend.prevent="endDraw"
        ></canvas>
      </div>

      <!-- 工具栏 -->
      <div class="inpaint-toolbar">
        <div class="inpaint-tool-group">
          <button
            type="button"
            class="inpaint-tool-btn"
            :class="{ active: !eraseMode }"
            @click="eraseMode = false"
            :title="'画笔'"
          >🖌️ 画笔</button>
          <button
            type="button"
            class="inpaint-tool-btn"
            :class="{ active: eraseMode }"
            @click="eraseMode = true"
            :title="'橡皮'"
          >🧽 橡皮</button>
        </div>

        <div class="inpaint-tool-group">
          <label class="inpaint-brush-label">笔刷大小</label>
          <input
            type="range"
            min="5"
            max="120"
            step="1"
            v-model.number="brushSize"
            class="inpaint-brush-range"
          >
          <span class="inpaint-brush-value">{{ brushSize }}</span>
        </div>

        <div class="inpaint-tool-group">
          <button type="button" class="inpaint-tool-btn" @click="undo" :disabled="!canUndo">↩️ 撤销</button>
          <button type="button" class="inpaint-tool-btn" @click="clearMask" :disabled="!hasMask">🗑️ 清空</button>
          <button type="button" class="inpaint-tool-btn" @click="reset">换图</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * InpaintCanvas —— 图像擦除涂抹组件
 *
 * 功能：
 *   1. 用户上传图片
 *   2. 在图上鼠标/触摸涂抹，标记要擦除的区域（半透明红色高亮）
 *   3. 画笔 / 橡皮 / 笔刷大小 / 撤销 / 清空
 *   4. getMask() 导出黑底白字 mask PNG（白色 = 擦除区，与原图同尺寸）
 *   5. getImage() 导出原图 Blob
 *
 * 抛出事件：
 *   @change  涂抹或清空后触发，参数 { hasMask, imageBlob, maskBlob }
 *            父组件在"开始擦除"按钮里调用 getMaskBlob() / getImageBlob() 即可
 */
export default {
  name: 'InpaintCanvas',
  props: {
    placeholderText: { type: String, default: '点击或拖拽上传图片，涂抹要擦除的区域' },
    maxDisplayWidth: { type: Number, default: 720 },
    maxDisplayHeight: { type: Number, default: 0 }
  },
  data: function () {
    return {
      imageLoaded: false,
      imageSrc: '',            // 原图 dataURL（预览用）
      imageBlob: null,         // 原图 Blob
      naturalWidth: 0,         // 原图真实宽高（canvas 用）
      naturalHeight: 0,
      displayWidth: 0,         // 画布显示宽高（CSS 用，等比缩放）
      displayHeight: 0,
      brushSize: 40,           // 笔刷大小（基于原图坐标）
      eraseMode: false,        // true=橡皮，false=画笔
      isDrawing: false,
      lastPoint: null,
      history: [],             // 撤销栈：保存 canvas ImageData 快照
      hasMask: false           // 是否已有涂抹
    }
  },
  computed: {
    canUndo: function () {
      return this.history.length > 0
    }
  },
  methods: {
    /* ===== 上传 ===== */
    handleFile: function (e) {
      var file = e.target.files && e.target.files[0]
      if (file) this.loadFile(file)
    },
    onDrop: function (e) {
      var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
      if (file) this.loadFile(file)
    },
    loadFile: function (file) {
      var self = this
      this.imageBlob = file
      var reader = new FileReader()
      reader.onload = function (ev) {
        self.imageSrc = ev.target.result
        var img = new Image()
        img.onload = function () {
          self.naturalWidth = img.naturalWidth
          self.naturalHeight = img.naturalHeight
          /* 同时限制显示宽高，避免竖图撑高所在工具面板；高度为 0 时保持原有行为。 */
          var widthScale = self.maxDisplayWidth / self.naturalWidth
          var heightScale = self.maxDisplayHeight > 0 ? self.maxDisplayHeight / self.naturalHeight : 1
          var scale = Math.min(1, widthScale, heightScale)
          self.displayWidth = Math.round(self.naturalWidth * scale)
          self.displayHeight = Math.round(self.naturalHeight * scale)

          self.imageLoaded = true
          self.hasMask = false
          self.history = []
          /* 等 DOM 渲染后初始化 canvas */
          self.$nextTick(function () {
            self.initCanvas()
            self.emitChange()
          })
        }
        img.src = self.imageSrc
      }
      reader.readAsDataURL(file)
    },

    /* ===== Canvas 初始化 ===== */
    initCanvas: function () {
      var canvas = this.$refs.drawCanvas
      if (!canvas) return
      var ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, this.naturalWidth, this.naturalHeight)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
    },

    /* ===== 坐标换算：把鼠标坐标（显示尺寸）转成原图坐标 ===== */
    getPoint: function (e) {
      var canvas = this.$refs.drawCanvas
      var rect = canvas.getBoundingClientRect()
      var clientX = e.clientX != null ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0)
      var clientY = e.clientY != null ? e.clientY : (e.touches && e.touches[0] ? e.touches[0].clientY : 0)
      /* 缩放比：显示宽 / 原图宽 */
      var scale = this.naturalWidth / rect.width
      return {
        x: (clientX - rect.left) * scale,
        y: (clientY - rect.top) * scale
      }
    },

    /* ===== 绘制 ===== */
    saveSnapshot: function () {
      var canvas = this.$refs.drawCanvas
      var ctx = canvas.getContext('2d')
      /* 限制历史栈深度，避免内存爆炸 */
      if (this.history.length >= 20) this.history.shift()
      this.history.push(ctx.getImageData(0, 0, canvas.width, canvas.height))
    },

    startDraw: function (e) {
      this.isDrawing = true
      this.saveSnapshot()
      var p = this.getPoint(e)
      this.lastPoint = p
      this.drawDot(p)
    },
    onMove: function (e) {
      if (!this.isDrawing) return
      var p = this.getPoint(e)
      this.drawLine(this.lastPoint, p)
      this.lastPoint = p
    },
    endDraw: function () {
      if (!this.isDrawing) return
      this.isDrawing = false
      this.lastPoint = null
      this.hasMask = true
      this.emitChange()
    },

    /* 在某点画一个圆点（笔刷 / 橡皮） */
    drawDot: function (p) {
      var ctx = this.$refs.drawCanvas.getContext('2d')
      ctx.save()
      ctx.globalCompositeOperation = this.eraseMode ? 'destination-out' : 'source-over'
      ctx.fillStyle = 'rgba(255,0,0,0.5)'
      ctx.beginPath()
      ctx.arc(p.x, p.y, this.brushSize / 2, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    },
    /* 在两点间画线段 */
    drawLine: function (from, to) {
      var ctx = this.$refs.drawCanvas.getContext('2d')
      ctx.save()
      ctx.globalCompositeOperation = this.eraseMode ? 'destination-out' : 'source-over'
      ctx.strokeStyle = 'rgba(255,0,0,0.5)'
      ctx.lineWidth = this.brushSize
      ctx.beginPath()
      ctx.moveTo(from.x, from.y)
      ctx.lineTo(to.x, to.y)
      ctx.stroke()
      ctx.restore()
    },

    /* ===== 撤销 / 清空 ===== */
    undo: function () {
      if (this.history.length === 0) return
      var snapshot = this.history.pop()
      var ctx = this.$refs.drawCanvas.getContext('2d')
      ctx.putImageData(snapshot, 0, 0)
      this.hasMask = this.checkHasContent()
      this.emitChange()
    },
    clearMask: function () {
      this.saveSnapshot()
      var ctx = this.$refs.drawCanvas.getContext('2d')
      ctx.clearRect(0, 0, this.naturalWidth, this.naturalHeight)
      this.hasMask = false
      this.emitChange()
    },
    reset: function () {
      this.imageLoaded = false
      this.imageSrc = ''
      this.imageBlob = null
      this.hasMask = false
      this.history = []
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
      this.emitChange()
    },

    /* 检测 canvas 是否有涂抹内容（用于撤销后更新 hasMask） */
    checkHasContent: function () {
      var ctx = this.$refs.drawCanvas.getContext('2d')
      var data = ctx.getImageData(0, 0, this.naturalWidth, this.naturalHeight).data
      /* 每 32 个像素采样一次，alpha>0 即有内容 */
      for (var i = 3; i < data.length; i += 128) {
        if (data[i] > 0) return true
      }
      return false
    },

    /* ===== 导出 ===== */
    /**
     * 导出白底黑字 mask（与原图同尺寸）
     * canvas 上是半透明红涂抹，这里转成纯黑涂抹 + 白底的灰度图
     * 约定：涂抹区（黑）= 要擦除，未涂抹区（白）= 保留
     * @returns {Promise<Blob>}
     */
    getMaskBlob: function () {
      var self = this
      return new Promise(function (resolve, reject) {
        if (!self.imageLoaded) {
          reject(new Error('未上传图片'))
          return
        }
        var src = self.$refs.drawCanvas
        var out = document.createElement('canvas')
        out.width = self.naturalWidth
        out.height = self.naturalHeight
        var ctx = out.getContext('2d')

        /* 白底（保留区） */
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, out.width, out.height)

        /* 从涂抹层取 alpha 通道作为黑涂抹（擦除区） */
        var srcCtx = src.getContext('2d')
        var srcData = srcCtx.getImageData(0, 0, src.width, src.height)
        var outData = ctx.getImageData(0, 0, out.width, out.height)
        var s = srcData.data
        var o = outData.data
        for (var i = 0; i < s.length; i += 4) {
          /* alpha>0 的位置 = 涂抹区，设为黑（擦除）；未涂抹保持白（保留） */
          var v = s[i + 3] > 0 ? 0 : 255
          o[i] = v
          o[i + 1] = v
          o[i + 2] = v
          o[i + 3] = 255
        }
        ctx.putImageData(outData, 0, 0)

        out.toBlob(function (blob) {
          if (blob) resolve(blob)
          else reject(new Error('生成 mask 失败'))
        }, 'image/png')
      })
    },
    getImageBlob: function () {
      return this.imageBlob
    },

    /* 通知父组件涂抹状态变化 */
    emitChange: function () {
      this.$emit('change', {
        hasMask: this.hasMask,
        imageBlob: this.imageBlob
      })
    }
  }
}
</script>

<style scoped>
.inpaint-canvas-wrap {
  width: 100%;
}

/* 上传区 */
.inpaint-upload {
  border: 2px dashed var(--border-color, #ddd);
  border-radius: 12px;
  padding: 64px 24px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
}
.inpaint-upload:hover {
  border-color: var(--primary, #5b8def);
}
.inpaint-upload-placeholder {
  color: var(--text-muted, #888);
}
.inpaint-upload-placeholder p {
  margin: 12px 0 4px;
  font-size: 16px;
  color: var(--text, #333);
}
.inpaint-upload-placeholder span {
  font-size: 13px;
}

/* 涂抹区 */
.inpaint-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.inpaint-stage {
  position: relative;
  margin: 0 auto;
  user-select: none;
  line-height: 0;
}
.inpaint-base-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 8px;
}
.inpaint-draw-canvas {
  position: absolute;
  top: 0;
  left: 0;
  cursor: crosshair;
}

/* 工具栏 */
.inpaint-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: var(--bg-secondary, #f7f7f8);
  border-radius: 8px;
}
.inpaint-tool-group {
  display: flex;
  align-items: center;
  gap: 8px;
}
.inpaint-tool-btn {
  padding: 6px 12px;
  border: 1px solid var(--border-color, #ddd);
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s;
}
.inpaint-tool-btn:hover:not(:disabled) {
  border-color: var(--primary, #5b8def);
}
.inpaint-tool-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.inpaint-tool-btn.active {
  background: var(--primary, #5b8def);
  color: #fff;
  border-color: var(--primary, #5b8def);
}
.inpaint-brush-label {
  font-size: 13px;
  color: var(--text-muted, #888);
}
.inpaint-brush-range {
  width: 120px;
}
.inpaint-brush-value {
  font-size: 13px;
  min-width: 28px;
  text-align: right;
}
</style>
